import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import {
  GLOBE_RADIUS,
  latLonToVector3,
  tangentBasis,
  magnitudeToSpread,
  magnitudeToParticleCount,
} from "../lib/seismic";
import { useSim } from "../lib/store";

const MAX = 12000;

const vertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uMaxSpread;
  uniform vec3 uEpicenter;
  uniform vec3 uT1;
  uniform vec3 uT2;
  uniform float uRadius;
  uniform float uMode;
  attribute float aAzimuth;
  attribute float aTarget;
  attribute float aSpeed;
  attribute float aPhase;
  attribute float aSize;
  attribute float aSeed;
  varying float vDist;
  varying float vFade;
  void main() {
    float p;
    float dist;
    if (uMode > 0.5) {
      float band = floor(aSeed * 4.0) / 4.0;
      p = fract(uTime * 0.12 + band);
      dist = uMaxSpread * p;
    } else {
      p = fract(uTime * aSpeed + aPhase);
      dist = aTarget * uMaxSpread * p;
    }
    vDist = clamp(dist / max(uMaxSpread, 0.001), 0.0, 1.0);
    vFade = sin(p * 3.14159265);
    vec3 dir = cos(aAzimuth) * uT1 + sin(aAzimuth) * uT2;
    vec3 sp = normalize(uEpicenter * cos(dist) + dir * sin(dist));
    float elev = (uMode > 0.5)
      ? (0.04 + 0.34 * vDist * (0.4 + 0.6 * vFade))
      : (0.015 + 0.05 * (1.0 - vDist));
    vec3 pos = sp * (uRadius + elev);
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_PointSize = aSize * vFade * (240.0 / -mv.z);
    gl_Position = projectionMatrix * mv;
  }
`;

const fragmentShader = /* glsl */ `
  uniform vec3 uCore;
  uniform vec3 uEdge;
  uniform vec3 uTsunami;
  uniform float uIntensity;
  uniform float uMode;
  varying float vDist;
  varying float vFade;
  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float d = length(uv);
    if (d > 0.5) discard;
    float alpha = smoothstep(0.5, 0.0, d);
    vec3 col = (uMode > 0.5) ? mix(uTsunami, uEdge, vDist) : mix(uCore, uEdge, vDist);
    gl_FragColor = vec4(col * uIntensity, alpha * vFade * 0.6);
  }
`;

export function EnergyParticles({ event }) {
  const pointsRef = useRef();

  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(MAX * 3);
    const aAzimuth = new Float32Array(MAX);
    const aTarget = new Float32Array(MAX);
    const aSpeed = new Float32Array(MAX);
    const aPhase = new Float32Array(MAX);
    const aSize = new Float32Array(MAX);
    const aSeed = new Float32Array(MAX);
    for (let i = 0; i < MAX; i++) {
      aAzimuth[i] = Math.random() * Math.PI * 2;
      aTarget[i] = Math.pow(Math.random(), 1.7);
      aSpeed[i] = 0.12 + Math.random() * 0.28;
      aPhase[i] = Math.random();
      aSize[i] = 4 + Math.random() * 9;
      aSeed[i] = Math.random();
    }
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geom.setAttribute("aAzimuth", new THREE.BufferAttribute(aAzimuth, 1));
    geom.setAttribute("aTarget", new THREE.BufferAttribute(aTarget, 1));
    geom.setAttribute("aSpeed", new THREE.BufferAttribute(aSpeed, 1));
    geom.setAttribute("aPhase", new THREE.BufferAttribute(aPhase, 1));
    geom.setAttribute("aSize", new THREE.BufferAttribute(aSize, 1));
    geom.setAttribute("aSeed", new THREE.BufferAttribute(aSeed, 1));
    geom.setDrawRange(0, 0);
    return geom;
  }, []);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uMaxSpread: { value: 0.5 },
      uEpicenter: { value: new THREE.Vector3(0, 1, 0) },
      uT1: { value: new THREE.Vector3(1, 0, 0) },
      uT2: { value: new THREE.Vector3(0, 0, 1) },
      uRadius: { value: GLOBE_RADIUS },
      uMode: { value: 0 },
      uCore: { value: new THREE.Color("#ffe6d0") },
      uEdge: { value: new THREE.Color("#b81414") },
      uTsunami: { value: new THREE.Color("#22c3e6") },
      uIntensity: { value: 1.4 },
    }),
    []
  );

  useFrame((state, delta) => {
    const st = useSim.getState();
    const points = pointsRef.current;
    if (!points || !event) return;

    const elapsed = (performance.now() - st.simStart) / 1000;
    const running = st.simulating && elapsed <= event.duration + 0.5;
    if (!running) {
      points.visible = false;
      return;
    }
    points.visible = true;

    uniforms.uTime.value += delta;

    const epi = latLonToVector3(event.lat, event.lon, 1).normalize();
    const { t1, t2 } = tangentBasis(epi);
    uniforms.uEpicenter.value.copy(epi);
    uniforms.uT1.value.copy(t1);
    uniforms.uT2.value.copy(t2);

    const targetSpread = magnitudeToSpread(event.magnitude);
    const smoothP = Math.min(elapsed / event.duration, 1);
    uniforms.uMaxSpread.value = targetSpread * (0.15 + 0.85 * smoothP);
    uniforms.uMode.value = st.tsunamiEnabled && event.offshore ? 1 : 0;
    uniforms.uIntensity.value = THREE.MathUtils.mapLinear(event.magnitude, 4, 9.5, 0.95, 2.3);

    const count = Math.min(MAX, magnitudeToParticleCount(event.magnitude, MAX));
    geometry.setDrawRange(0, count);
  });

  return (
    <points ref={pointsRef} geometry={geometry} frustumCulled={false} visible={false}>
      <shaderMaterial
        uniforms={uniforms}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
